import { StateService } from "@uirouter/angular";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest } from "rxjs";
import { getCurrentUserId } from "@gtmhub/users";
import { AnalyticsModule } from "@webapp/analytics/analytics.module";
import { TrackingMetadata } from "@webapp/analytics/models/analytics.model";
import { AssigneesModule } from "@webapp/assignees/assignees.module";
import { AssigneeType } from "@webapp/assignees/models/assignee.models";
import { WidgetEmptyStateComponent } from "@webapp/home/components/widgets/widget-empty-state/widget-empty-state.component";
import { WidgetSkeletonComponent } from "@webapp/home/components/widgets/widget-skeleton/widget-skeleton.component";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { IsNavigationExpandedCache } from "@webapp/navigation/services/is-navigation-expanded.cache";
import { SessionSummaryReportComponent } from "@webapp/okrs/components/assignee-active-okrs-list/session-summary-report/session-summary-report.component";
import { MetricProgressBarComponent } from "@webapp/okrs/metrics/components/metric-progress-bar/metric-progress-bar.component";
import { OkrsCoreModule } from "@webapp/okrs/okrs-core.module";
import { HasPermissionsDirective } from "@webapp/permissions/directives/has-permissions.directive";
import { ConfidenceLabelComponent } from "@webapp/shared/components/confidence-label/confidence-label.component";
import { UiBadgeModule } from "@webapp/ui/badge/badge.module";
import { UiCardModule } from "@webapp/ui/card/card.module";
import { UiCollapseModule } from "@webapp/ui/collapse/collapse.module";
import { UiIconModule } from "@webapp/ui/icon/icon.module";
import { UiLoadingIndicatorModule } from "@webapp/ui/loading-indicator/loading-indicator.module";
import { UiProgressModule } from "@webapp/ui/progress/progress.module";
import { WidgetActionsComponent } from "../../../home/components/widgets/widget-actions/widget-actions.component";
import { CreateOkrSplitButtonComponent } from "../create-okr-split-button/create-okr-split-button.component";
import { AssigneeActiveOkrsListStateDirective } from "./assignee-active-okrs-list-state.directive";
import { OkrGridViewMode, OkrsSessionGroup, SectionExpandState } from "./assignee-active-okrs-list.models";
import { AssigneeActiveOkrsRepository } from "./repository/assignee-active-okrs-repository.service";

const customBreakpoints: Record<string, string> = {
  Small: "(max-width: 1023.98px)",
  Medium: "(min-width: 1024px) and (max-width: 1279.98px)",
  Large: "(min-width: 1280px) and (max-width: 1439.98px)",
  XLarge: "(min-width: 1440px)",
};

@UntilDestroy()
@Component({
  selector: "assignee-active-okrs-list",
  templateUrl: "./assignee-active-okrs-list.component.html",
  styleUrls: ["./assignee-active-okrs-list.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    UiCardModule,
    UiCollapseModule,
    UiBadgeModule,
    UiIconModule,
    LocalizationModule,
    CommonModule,
    OkrsCoreModule,
    AnalyticsModule,
    UiLoadingIndicatorModule,
    ConfidenceLabelComponent,
    MetricProgressBarComponent,
    AssigneesModule,
    WidgetActionsComponent,
    SessionSummaryReportComponent,
    UiProgressModule,
    WidgetEmptyStateComponent,
    CreateOkrSplitButtonComponent,
    WidgetSkeletonComponent,
    HasPermissionsDirective,
    AssigneeActiveOkrsListStateDirective,
  ],
})
export class AssigneeActiveOkrsListComponent implements OnInit {
  @Input() public assigneeId: string;
  @Input() public assigneeType: AssigneeType;
  @Input() public trackMeta: TrackingMetadata;

  public viewMode: OkrGridViewMode;
  public sectionsState: Record<string, SectionExpandState> = {};
  public progressBarState: Record<string, { hideBar?: boolean; hideDelta?: boolean }> = {};
  public data: OkrsSessionGroup[] = [];
  public loading = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private isNavigationExpandedCache: IsNavigationExpandedCache,
    private assigneeActiveOkrsRepository: AssigneeActiveOkrsRepository,
    private state: StateService,
    private cdr: ChangeDetectorRef
  ) {
    if (!this.viewMode) {
      this.setBreakpointObserver();
    }
  }

  public ngOnInit(): void {
    this.loading = true;
    this.assigneeActiveOkrsRepository
      .getAll$({
        config: {
          queryParams: {
            assigneeId: this.assigneeId,
          },
        },
      })
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.data = data.items.filter((session) => session.items?.length);
        this.loading = false;

        this.setDefaultSectionsState();
        this.cdr.markForCheck();
      });
  }

  public stateChanged(sessionId: string, state: SectionExpandState): void {
    this.sectionsState[sessionId] = state;
    this.setProgressState();
    this.cdr.detectChanges();
  }

  private setDefaultSectionsState(): void {
    if (!this.data.length) {
      return;
    }

    this.sectionsState[this.data[0].id] = {
      panel: true,
      summary: false,
    };
  }

  public openGoal(id: string): void {
    this.state.go("._goals._goalSidebar.goal", { id: id });
  }

  public openMetric(id: string): void {
    this.state.go("._metric.metric", { metricId: id });
  }

  private setBreakpointObserver(): void {
    combineLatest([
      this.isNavigationExpandedCache.get$(),
      this.breakpointObserver.observe([customBreakpoints.Small, customBreakpoints.Medium, customBreakpoints.Large, customBreakpoints.XLarge]),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isNavExpanded, state]: [boolean, BreakpointState]) => {
        if (!state.matches) {
          return;
        }

        if (state.breakpoints[customBreakpoints.Small]) {
          this.viewMode = isNavExpanded ? "x-small" : "small";
        }
        if (state.breakpoints[customBreakpoints.Medium]) {
          this.viewMode = isNavExpanded ? "medium" : "large";
        }
        if (state.breakpoints[customBreakpoints.Large]) {
          this.viewMode = isNavExpanded ? "large" : "x-large";
        }
        if (state.breakpoints[customBreakpoints.XLarge]) {
          this.viewMode = "x-large";
        }

        this.setProgressState();
        this.cdr.markForCheck();
      });
  }

  private setProgressState(): void {
    Object.keys(this.sectionsState).forEach((sessionId) => {
      switch (this.viewMode) {
        case "x-small":
        case "small":
          this.progressBarState[sessionId] = {
            hideBar: true,
            hideDelta: true,
          };
          break;
        case "medium":
          this.progressBarState[sessionId] = {
            hideBar: true,
            hideDelta: this.sectionsState[sessionId].summary,
          };
          break;
        case "large":
          this.progressBarState[sessionId] = {
            hideBar: this.sectionsState[sessionId].summary,
            hideDelta: this.sectionsState[sessionId].summary,
          };
          break;
        case "x-large":
          this.progressBarState[sessionId] = {
            hideBar: false,
            hideDelta: false,
          };
          break;
      }
    });
  }

  public getTrackingMetadata(sessionId: string): TrackingMetadata {
    const group = this.data.find((item) => item.id === sessionId);

    return {
      button_name: "session_accordion",
      expanded: !this.sectionsState[sessionId]?.panel,
      session_id: sessionId,
      assignee_id: this.assigneeId,
      assignee_type: this.assigneeType,
      user_id: getCurrentUserId(),
      okrs_count: group.items.length,
      ...this.trackMeta,
    };
  }

  public getSummaryReportExtraTrackingMetadata(sessionId: string): TrackingMetadata {
    const group = this.data.find((item) => item.id === sessionId);

    return {
      assignee_id: this.assigneeId,
      assignee_type: this.assigneeType,
      okrs_count: group.items.length,
      ...this.trackMeta,
    };
  }
}
